import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MainFeed } from '../../models/feed.model';
import { CreateMainFeedRequest, UpdateMainFeedRequest } from '../../models/main-feed-service.model';

@Injectable({
    providedIn: 'root'
})
export class MainFeedService {
    private BASE_URL = `${environment.origins.feedsSupremeCommander}/api/MainFeeds`;
    private _mainFeedId$ = new BehaviorSubject<string>('');
    mainFeedId$ = this._mainFeedId$.asObservable();

    private http = inject(HttpClient);

    saveMainFeed(saveFeedRequest: CreateMainFeedRequest): Observable<string> {
        return this.http.post(this.BASE_URL, saveFeedRequest, { responseType: 'text' }).pipe(
            map(newMainFeedId => {
                this._mainFeedId$.next(newMainFeedId);

                return saveFeedRequest.name;
            }),
            catchError(err => this.handleError(err, saveFeedRequest.name))
        );
    }

    setMainFeedId(id: string) {
        this._mainFeedId$.next(id);
    }

    updateMainFeed(mainFeed: UpdateMainFeedRequest): Observable<string> {
        const body = {
            name: mainFeed.name,
            updateInterval: mainFeed.updateInterval,
            fields: mainFeed.blueprint.fields,
            feedFilterGroups: mainFeed.filters?.filterGroups || [],
            textReplaceOperations: mainFeed.textReplaceOperations || [],
            sourceUrl: mainFeed.sourceUrl,
            previewSourceId: mainFeed.previewSourceId,
            rootArrayPath: mainFeed.rootArrayPath
        };

        return this.http
            .put(`${this.BASE_URL}/${mainFeed.id}`, body, {
                responseType: 'text'
            })
            .pipe(catchError(err => this.handleError(err, mainFeed.name)));
    }

    getAllMainFeeds(): Observable<MainFeed[]> {
        return this.http
            .get<MainFeed[]>(this.BASE_URL)
            .pipe(catchError(err => throwError(() => new Error('Failed to get main feeds. Please try again later.'))));
    }

    getMainFeedById(id: string): Observable<Required<MainFeed>> {
        return this.http
            .get<Required<MainFeed>>(`${this.BASE_URL}/${id}`)
            .pipe(catchError(err => throwError(() => new Error('Failed to get main feed with id: ' + id))));
    }

    triggerUpdate(id: string) {
        return this.http
            .post(`${this.BASE_URL}/${id}/triggerUpdate`, {})
            .pipe(
                catchError(err => throwError(() => new Error('Failed to trigger update for main feed with id: ' + id)))
            );
    }

    private handleError(error: HttpErrorResponse, templateName: string, id?: string) {
        if (error.status === 404) {
            // Handle 404 errors differently if needed
            return throwError(() => new Error('Endpoint not found. Please contact support.'));
        }
        // Return a more generic message for other errors
        return throwError(
            () =>
                new Error(
                    `An error occurred while ${id ? 'updating' : 'saving'} ${templateName}. Please try again later.`
                )
        );
    }
}
